import { useLayoutEffect, useRef } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { DeferredOverlay } from './DeferredOverlay';
import { Icon } from './Icon';
import { MOBILE_OVERFLOW, MOBILE_PRIMARY, navAlsoActive, visibleNav, type NavItem } from './nav';
import { UnreadMailBadge } from '../features/inbox/UnreadMail';
import { useSession } from '../features/auth/session';
import { TAB_ICON, TAB_LABEL, tabItem } from './railItem';
import { alertBadgeTone, useRailBadges } from '../features/alerts/useRailBadges';

/** Does this path sit inside that section? The bar has to know without a `<Link>` to ask it,
 *  because the More tab is not a link — it opens a sheet. `/` is exact, or everything matches. */
export function inSection(pathname: string, to: string): boolean {
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * The rail, on a phone.
 *
 * A 76px column down the side of a 390px screen spends a fifth of the width on chrome, so
 * below `md` the same navigation lies along the bottom edge instead — where a thumb is. It is
 * a genuinely different control rather than the rail reflowed: a handful of tabs of equal width with
 * their labels under their icons, and everything that does not fit behind a More sheet.
 *
 * The two surfaces share `nav.ts` and the geometry in `railItem.ts`, and only ever one of
 * them is displayed (`hidden md:flex` against `md:hidden`), so nothing is announced twice.
 */
export function MobileNav() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const roles = useSession()?.roles ?? [];
  const badges = useRailBadges();
  const unread = badges?.alerts.unread ?? 0;
  /*
   * What is waiting behind More: the notifications row always, and unread mail when Communications
   * is one of the places in the sheet rather than on the bar.
   */
  const unreadMailBehind = MOBILE_OVERFLOW.some((item) => item.badge === 'unread-mail')
    ? (badges?.unreadMail ?? 0)
    : 0;
  const waiting = unread + unreadMailBehind;
  const bar = useRef<HTMLElement>(null);
  const highlight = useRef<HTMLSpanElement>(null);

  /*
   * Publish the room the bar needs, because it no longer takes that room itself.
   *
   * The same arrangement `TopBar` uses and for the same reason: a fixed element occupies no
   * space, so the scroll container has to reserve it, and CSS cannot ask how tall a sibling is.
   * Measuring rather than deriving — the height is a tab's min-height plus padding plus a
   * safe-area inset that only exists on some hardware, and that arithmetic would be wrong on
   * the first phone that disagreed with it.
   *
   * On desktop the bar is `display:none`, which measures 0 and therefore reserves nothing, so
   * the same property is correct at every width with no breakpoint in the reader.
   */
  useLayoutEffect(() => {
    const el = bar.current;
    if (!el) return;

    const publish = () => {
      const box = el.getBoundingClientRect();
      // The gap below the bar is part of what must be cleared: content scrolling to the very
      // bottom should come to rest clear of the bar, not tuck into the gap under it.
      const clearance = box.height === 0 ? 0 : window.innerHeight - box.top;
      document.documentElement.style.setProperty('--mobilenav-height', `${clearance}px`);
    };

    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    window.addEventListener('resize', publish);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', publish);
      // Back to the token, so a shell without the bar cannot inherit the last measurement.
      document.documentElement.style.removeProperty('--mobilenav-height');
    };
  }, []);

  // Settings is reached from the sheet's account panel, so it counts as "in More" as well —
  // otherwise the bar claims you are in no section at all while you are sitting in one.
  const inOverflow =
    inSection(pathname, '/settings') ||
    MOBILE_OVERFLOW.some((item) => inSection(pathname, item.to));

  /*
   * The selected tab's fill: ONE element for the whole bar, slid under whichever tab is current.
   *
   * Each tab used to paint its own `bg-accent-soft`, so changing section switched one off and
   * another on in the same frame — nothing to animate between. `TabStrip`'s arrangement, and its
   * rule: TRANSFORM ONLY (DESIGN_SYSTEM §6). The fill takes the new tab's size at once and starts
   * translated and scaled back to the old one; the transition plays that out to nothing.
   *
   * Simpler than `TabStrip` in one way: the bar belongs to the shell and survives navigation, so
   * the previous box is still on the element — there is no handoff between instances to arrange.
   */
  useLayoutEffect(() => {
    const nav = bar.current;
    const pill = highlight.current;
    if (!nav || !pill) return;
    const selected = nav.querySelector<HTMLElement>('[data-tab-selected]');
    if (!selected) {
      pill.style.opacity = '0';
      delete pill.dataset.box;
      return;
    }

    const measure = (): Box => ({
      left: selected.offsetLeft,
      top: selected.offsetTop,
      width: selected.offsetWidth,
      height: selected.offsetHeight,
    });
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const place = (box: Box, animate: boolean) => {
      const from = pill.dataset.box ? (JSON.parse(pill.dataset.box) as Box) : null;
      // `display: none` above `md` measures zero; there is nothing to place, or to slide from.
      if (box.width === 0) return;
      if (from && sameBox(from, box)) return;
      pill.style.width = `${String(box.width)}px`;
      pill.style.height = `${String(box.height)}px`;
      // Appearing from nothing, or the same tab resizing, moves without sliding.
      const slide = animate && from !== null && !reduced;
      pill.style.opacity = '1';
      pill.style.transition = 'none';
      if (slide) {
        pill.style.transform = `translate(${String(from.left)}px, ${String(from.top)}px) scaleX(${String(from.width / box.width)})`;
        // Commit the starting transform before the transition is switched back on, or there is
        // nothing to move from.
        void pill.offsetWidth;
        pill.style.transition = '';
      }
      pill.style.transform = `translate(${String(box.left)}px, ${String(box.top)}px)`;
      pill.dataset.box = JSON.stringify(box);
    };

    place(measure(), true);
    // Tabs share the width evenly, so rotating the phone or a font loading moves every one of
    // them without the selection changing.
    const observer = new ResizeObserver(() => place(measure(), false));
    observer.observe(nav);
    return () => observer.disconnect();
  }, [pathname, inOverflow]);

  return (
    <nav
      ref={bar}
      aria-label="Main"
      // Chrome, not text: no long-press menu or selection on a touchscreen — see styles.css.
      data-chrome=""
      /*
       * A RAISED GLASS BAR, floating clear of the bottom edge.
       *
       * It used to be a flex sibling of <main>, flush to the edge and opaque, which kept the
       * layout simple: the bar reserved its own space and nothing could end up beneath it.
       * Glass is what changed that. DESIGN_SYSTEM §2 allows a translucent surface only where it
       * overlays other content — "blurring the page behind an ordinary panel costs paint time
       * and buys nothing" — so a bar that is genuinely glass has to be lifted out of the flow
       * and have the page run underneath it. The reservation it stopped making for itself is
       * `--mobilenav-height`, published by the effect above and cleared by <main>.
       *
       * `glass-strong` rather than `glass`: these are labels, and §2 puts body copy on the 0.9
       * surface because the content sliding underneath is arbitrary — a dense table one moment
       * and an empty state the next — and legibility cannot depend on which.
       *
       * The shadow is the one `.glass` already carries. §2b warns against shadows heavy enough
       * that a surface looks detached from the screen, and a floating bar is exactly where that
       * temptation lives, so it gets the system's own elevation rather than a deeper one.
       *
       * `bottom` clears the iOS home indicator instead of padding doing it. The indicator sits
       * INSIDE the layout viewport, and a floating bar belongs above it — padding would have
       * stretched the glass down behind it instead.
       */
      className="glass glass-strong fixed inset-x-3 bottom-[max(env(safe-area-inset-bottom),0.5rem)] z-30 isolate flex items-stretch gap-0.5 rounded-pill p-1 md:hidden"
    >
      {/* Behind the tabs via `-z-10` in the bar's `isolate` context, so it never covers a label. */}
      <span
        ref={highlight}
        aria-hidden="true"
        className="pointer-events-none absolute top-0 left-0 -z-10 origin-top-left rounded-pill bg-accent-soft opacity-0 transition-transform duration-[var(--duration-slow)] ease-[var(--ease-out)] motion-reduce:transition-none"
      />

      {visibleNav(MOBILE_PRIMARY, roles).map((item) => (
        <Tab key={item.to} item={item} />
      ))}

      <DeferredOverlay
        overlay="MoreSheet"
        failureTitle="Could not open the menu"
        triggerProps={{
          className: tabItem(inOverflow),
          ...(inOverflow ? { 'data-tab-selected': '' } : {}),
          // The unread state belongs in the accessible name, not only in the dot: the
          // notifications row lives inside this sheet, so this tab is what carries it.
          'aria-label':
            waiting === 0
              ? 'More'
              : `More (${[
                  unread > 0 ? `${String(unread)} unread notifications` : null,
                  unreadMailBehind > 0 ? `${String(unreadMailBehind)} unread emails` : null,
                ]
                  .filter((part) => part !== null)
                  .join(', ')})`,
        }}
      >
        <span className="relative flex items-center justify-center">
          <Icon name="more" size={TAB_ICON} />
          {waiting > 0 ? (
            <span
              aria-hidden="true"
              // Red only for something urgent — see `alertBadgeTone`.
              className={`absolute -top-0.5 -right-1 size-2 rounded-pill ring-2 ring-surface ${
                alertBadgeTone(badges?.alerts.urgent ?? 0) === 'danger' ? 'bg-danger' : 'bg-accent'
              }`}
            />
          ) : null}
        </span>
        <span className={TAB_LABEL}>More</span>
      </DeferredOverlay>
    </nav>
  );
}

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

function sameBox(a: Box, b: Box): boolean {
  return a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height;
}

function Tab({ item }: { item: NavItem }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  // Decided here rather than by the router's `activeProps`, because the fill finds its tab by
  // `data-tab-selected` and the two must never disagree. `inSection` is the router's own default
  // for a link with no search — a path prefix, exact only for `/`.
  const selected = inSection(pathname, item.to) || navAlsoActive(item, pathname);

  return (
    <Link
      to={item.to}
      activeOptions={{ exact: item.to === '/' }}
      // The visible text is shortened to fit a ~78px tab; the full name is what is announced.
      aria-label={item.label}
      className={tabItem(selected)}
      {...(selected ? { 'aria-current': 'page' as const, 'data-tab-selected': '' } : {})}
    >
      <span className="relative">
        <Icon name={item.icon} size={TAB_ICON} />
        {item.badge === 'unread-mail' ? (
          <UnreadMailBadge className="absolute -top-1.5 left-3.5" />
        ) : null}
      </span>
      <span className={TAB_LABEL}>{item.short ?? item.label}</span>
    </Link>
  );
}
